import React, { useEffect } from 'react';
import { getDocBySlug } from './trustCenterContent';
import { FullLegalDisclaimer } from './Disclaimer';

// Renders bullet lists.
function Bullets({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul className="list-disc list-outside pl-5 space-y-1.5 text-sm sm:text-base text-gray-700">
      {items.map((b, i) => (
        <li key={i}>{b}</li>
      ))}
    </ul>
  );
}

// Renders an array (or single string) of paragraphs.
function Paragraphs({ body }) {
  if (!body) return null;
  const paras = Array.isArray(body) ? body : [body];
  return (
    <>
      {paras.map((p, i) => (
        <p key={i} className="text-sm sm:text-base text-gray-700 leading-relaxed">
          {p}
        </p>
      ))}
    </>
  );
}

// Renders the CCPA-style disclosure table.
function DocTable({ table }) {
  if (!table) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full text-sm text-left">
        <thead className="bg-gray-50">
          <tr>
            {table.headers.map((h, i) => (
              <th
                key={i}
                className="px-4 py-2.5 font-semibold text-gray-900 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {table.rows.map((row, ri) => (
            <tr key={ri} className="align-top">
              {row.map((cell, ci) => (
                <td key={ci} className="px-4 py-3 text-gray-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ sec }) {
  return (
    <section className="mt-8 space-y-3">
      {sec.heading && (
        <h2 className="text-base sm:text-lg font-bold text-gray-900">
          {sec.heading}
        </h2>
      )}
      <Paragraphs body={sec.body} />
      <Bullets items={sec.bullets} />
      <DocTable table={sec.table} />
      {(sec.subsections || []).map((sub, i) => (
        <div key={i} className="mt-4 space-y-2 pl-0 sm:pl-1">
          {sub.subheading && (
            <h3 className="text-sm sm:text-base font-semibold text-indigo-900">
              {sub.subheading}
            </h3>
          )}
          <Paragraphs body={sub.body} />
          <Bullets items={sub.bullets} />
          <DocTable table={sub.table} />
        </div>
      ))}
    </section>
  );
}

// Generic Trust Center document page. `slug` selects which document to render
// (supports legacy aliases). `onBack` returns to the Trust Center landing page.
export default function LegalPage({ slug, onBack }) {
  const doc = getDocBySlug(slug);

  // Reset scroll to the top whenever a new document opens. Without this, the
  // window keeps the scroll offset from the (long) Trust Center landing page,
  // so the document appears to open partway down / at the bottom.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [slug]);

  if (!doc) {
    return (
      <div className="p-8 text-center text-gray-700">
        Document not found.{' '}
        <button
          onClick={onBack}
          className="text-indigo-600 hover:text-indigo-800 underline"
        >
          Back to Trust Center
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-10 px-3 sm:px-4">
      <div className="max-w-3xl mx-auto bg-white shadow-sm rounded-2xl p-5 sm:p-8">
        {/* Header row */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <button
            onClick={onBack}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-semibold"
          >
            &larr; Trust Center
          </button>
          {doc.pdf && (
            <a
              href={doc.pdf}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-4 py-2 rounded-lg shadow-sm transition-colors"
            >
              <span aria-hidden="true">{'\u2B07\uFE0F'}</span> Download PDF
            </a>
          )}
        </div>

        {/* Title block */}
        <header className="border-b border-gray-100 pb-5">
          <h1 className="text-2xl sm:text-3xl font-bold text-indigo-900 break-words">
            {doc.cardTitle}
          </h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
            <span>Last Updated: {doc.lastUpdated}</span>
            {doc.version && (
              <>
                <span className="text-gray-300">|</span>
                <span>Version {doc.version}</span>
              </>
            )}
            <span className="text-gray-300">|</span>
            <span
              className={`uppercase tracking-wider font-semibold ${
                doc.type === 'legal' ? 'text-indigo-600' : 'text-emerald-600'
              }`}
            >
              {doc.type === 'legal' ? 'Legal Document' : 'Educational'}
            </span>
          </div>
        </header>

        {/* Intro */}
        {doc.intro && doc.intro.length > 0 && (
          <div className="mt-5 space-y-3">
            <Paragraphs body={doc.intro} />
          </div>
        )}

        {/* Sections */}
        {(doc.sections || []).map((sec, i) => (
          <Section key={i} sec={sec} />
        ))}

        {/* Standard disclaimer on legal docs */}
        {doc.type === 'legal' && (
          <div className="mt-10">
            <FullLegalDisclaimer />
          </div>
        )}

        {/* Footer return link */}
        <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
          <button
            onClick={onBack}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-semibold"
          >
            &larr; Return to Trust Center
          </button>
          {doc.pdf && (
            <a
              href={doc.pdf}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
            >
              Download PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
